import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Header menu — arrow-only submenu toggle.
 *
 * Behavior:
 *   - Anchor (icon + text) click → follows href, navigates to collection page
 *   - Arrow button click → toggles submenu, no navigation
 *   - "More" button → handled by theme's overflow-list
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;

  /** @type {MutationObserver | null} */
  #arrowObserver = null;

  /** @type {State} */
  #state = { activeItem: null };

  connectedCallback() {
    super.connectedCallback();
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#onDocumentClick, false);
    document.addEventListener('keydown', this.#onKeyDown);

    this.#bindArrowListeners();

    // Re-bind whenever arrows are added/replaced (overflow, hydration).
    this.#arrowObserver = new MutationObserver(() => this.#bindArrowListeners());
    this.#arrowObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onKeyDown);
    this.#cleanupMutationObserver();
    this.#arrowObserver?.disconnect();
    this.#arrowObserver = null;
  }

  #resizeListener = debounce(() => setHeaderMenuStyle(), 100);

  /**
   * Attach a native click listener to every unbound arrow button.
   * Idempotent — marked with data-arrow-bound.
   */
  #bindArrowListeners() {
    const arrows = this.querySelectorAll('.menu-list__link-arrow:not([data-arrow-bound])');
    arrows.forEach((arrow) => {
      arrow.dataset.arrowBound = 'true';
      arrow.addEventListener('click', this.#onArrowClick);
    });
  }

  /**
   * Arrow click → toggle submenu. No navigation.
   * @param {MouseEvent} event
   */
  #onArrowClick = (event) => {
    const arrow = event.currentTarget;
    if (!(arrow instanceof HTMLElement)) return;

    const li = arrow.closest('.menu-list__list-item');
    if (!li) return;

    const anchor = li.querySelector('[ref="menuitem"]');
    const submenu = li.querySelector('[ref="submenu[]"]');
    if (!anchor || !submenu) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    // Toggle off if already open.
    if (this.#state.activeItem === anchor) {
      this.#deactivate();
      return;
    }

    this.#openItem(anchor, arrow, submenu);
  };

  /**
   * Document click — close submenu when clicking outside the header-menu.
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#deactivate();
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape') this.#deactivate();
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Kept for the "More" button (theme's overflow-list handles it internally).
   */
  activate = () => {
    // No-op for regular items now — they use the arrow button.
    // The "More" button is handled by the theme's overflow-list component.
  };

  /**
   * Open the submenu for the given anchor.
   * @param {HTMLElement} anchor
   * @param {HTMLElement} arrow
   * @param {HTMLElement} submenu
   */
  #openItem(anchor, arrow, submenu) {
    if (!this.headerComponent) return;

    // Close any previously open submenu first.
    const previous = this.#state.activeItem;
    if (previous) {
      previous.setAttribute('aria-expanded', 'false');
      const prevLi = previous.closest('.menu-list__list-item');
      prevLi?.querySelector('.menu-list__link-arrow')?.setAttribute('aria-expanded', 'false');
      const prevSubmenu = prevLi?.querySelector('[ref="submenu[]"]');
      if (prevSubmenu) delete prevSubmenu.dataset.active;
    }

    this.#state.activeItem = anchor;
    this.setAttribute('aria-expanded', 'true');
    anchor.setAttribute('aria-expanded', 'true');
    arrow.setAttribute('aria-expanded', 'true');
    this.dataset.overflowExpanded = 'false';

    submenu.dataset.active = '';
    this.dispatchEvent(new MegaMenuHoverEvent());

    // Measure the submenu once its content settles.
    this.#cleanupMutationObserver();
    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.#applyHeight(submenu);
            this.#cleanupMutationObserver();
          }
        });
      });
    });
    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    setTimeout(() => this.#cleanupMutationObserver(), 500);

    this.#applyHeight(submenu);
  }

  #applyHeight(submenu) {
    if (!this.headerComponent) return;
    const headerVisible = this.headerComponent.offsetHeight || 0;
    const h = submenu.offsetHeight || 0;
    this.headerComponent.style.setProperty('--submenu-height', `${h}px`);
    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${h === 0 ? 0 : h + headerVisible}px`
    );
  }

  deactivate = () => this.#deactivate();

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.headerComponent?.style.setProperty('--full-open-header-height', '0px');
    this.dataset.overflowExpanded = 'false';

    const li = item.closest('.menu-list__list-item');
    const submenu = li?.querySelector('[ref="submenu[]"]');
    const arrow = li?.querySelector('.menu-list__link-arrow');

    this.#state.activeItem = null;
    this.setAttribute('aria-expanded', 'false');
    item.setAttribute('aria-expanded', 'false');
    arrow?.setAttribute('aria-expanded', 'false');
    if (submenu) delete submenu.dataset.active;
  };

  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((img) =>
      img.removeAttribute('loading')
    );
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}